(() => {
  if (customElements.get('nny-product')) return;
  // Native horizontal scrolling for touch/trackpads; pointer dragging for a mouse.
  const setupGallery = (root, signal) => {
    const track = root.querySelector('[data-gallery-track]');
    const slides = [...root.querySelectorAll('[data-media]')];
    const thumbs = [...root.querySelectorAll('[data-media-target]')];
    if (!track || !slides.length) return () => {};
    const previous = root.querySelector('[data-gallery-prev]');
    const next = root.querySelector('[data-gallery-next]');
    const counter = root.querySelector('[data-gallery-count]');
    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    let index = Math.max(0, thumbs.findIndex(button => button.getAttribute('aria-pressed') === 'true'));
    let destination = null, drag = null, frame = 0, scrollTimer = 0;
    const clamp = value => Math.max(0, Math.min(slides.length - 1, value));
    const pause = slide => {
      slide.querySelectorAll('video').forEach(video => video.pause());
      slide.querySelectorAll('iframe').forEach(iframe => {
        try {
          const url = new URL(iframe.src, location.href);
          if (/(^|\.)youtube(?:-nocookie)?\.com$/.test(url.hostname)) iframe.contentWindow?.postMessage(JSON.stringify({event:'command',func:'pauseVideo',args:[]}), url.origin);
          else if (url.hostname === 'player.vimeo.com') iframe.contentWindow?.postMessage(JSON.stringify({method:'pause'}), url.origin);
        } catch { /* A media frame without a URL has nothing to pause. */ }
      });
      slide.querySelectorAll('model-viewer').forEach(model => model.pause?.());
    };
    const fitActiveMedia = () => {
      const slide = slides[index];
      const image = slide.querySelector('img');
      const ratio = Number(slide.dataset.mediaRatio) ||
        (image?.naturalWidth / image?.naturalHeight) ||
        (Number(image?.getAttribute('width')) / Number(image?.getAttribute('height')));
      if (Number.isFinite(ratio) && ratio > 0) track.style.setProperty('--np-gallery-ratio', String(ratio));
    };
    slides.forEach((slide, i) => slide.querySelector('img')?.addEventListener('load', () => {
      if (i === index) fitActiveMedia();
    }, {signal}));
    const markActive = value => {
      index = clamp(value);
      fitActiveMedia();
      slides.forEach((slide, i) => {
        slide.inert = i !== index;
        if (i !== index) pause(slide);
      });
      thumbs.forEach((button, i) => button.setAttribute('aria-pressed', String(i === index)));
      if (previous) previous.disabled = index === 0;
      if (next) next.disabled = index === slides.length - 1;
      if (counter) counter.textContent = `${String(index + 1).padStart(2, '0')} / ${String(slides.length).padStart(2, '0')}`;
      const thumb = thumbs[index], strip = thumb?.parentElement;
      if (thumb && strip) {
        const item = thumb.getBoundingClientRect(), bounds = strip.getBoundingClientRect();
        if (item.left < bounds.left) strip.scrollLeft -= bounds.left - item.left;
        else if (item.right > bounds.right) strip.scrollLeft += item.right - bounds.right;
      }
    };
    const goTo = (value, smooth = true) => {
      destination = clamp(value);
      markActive(destination);
      track.scrollTo({left: destination * track.clientWidth, behavior: smooth && !motion.matches ? 'smooth' : 'instant'});
    };
    const settle = () => {
      if (drag?.moving || !track.clientWidth) return;
      destination = null;
      markActive(Math.round(track.scrollLeft / track.clientWidth));
    };
    track.addEventListener('scroll', () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(settle, 160);
      if (frame || drag?.moving) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        if (destination === null && track.clientWidth) {
          const current = clamp(Math.round(track.scrollLeft / track.clientWidth));
          if (current !== index) markActive(current);
        }
      });
    }, {passive:true, signal});
    track.addEventListener('scrollend', settle, {signal});
    previous?.addEventListener('click', () => goTo((destination ?? index) - 1), {signal});
    next?.addEventListener('click', () => goTo((destination ?? index) + 1), {signal});
    thumbs.forEach((button, i) => button.addEventListener('click', () => goTo(i), {signal}));
    track.addEventListener('keydown', event => {
      if (event.target !== track) return;
      const value = {ArrowLeft:index - 1, ArrowRight:index + 1, Home:0, End:slides.length - 1}[event.key];
      if (value === undefined) return;
      event.preventDefault(); goTo(value);
    }, {signal});
    track.addEventListener('dragstart', event => { if (event.target.tagName === 'IMG') event.preventDefault(); }, {signal});
    track.addEventListener('pointerdown', event => {
      destination = null;
      if (event.pointerType !== 'mouse' || event.button !== 0 || slides.length < 2 || event.target.closest('video,iframe,model-viewer,button,a,input')) return;
      drag = {id:event.pointerId, x:event.clientX, y:event.clientY, left:track.scrollLeft, index, moving:false};
      track.setPointerCapture(event.pointerId);
    }, {signal});
    track.addEventListener('pointermove', event => {
      if (!drag || event.pointerId !== drag.id) return;
      const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
      if (!drag.moving) {
        if (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy)) return;
        drag.moving = true;
        track.classList.add('is-dragging');
      }
      event.preventDefault();
      track.scrollLeft = drag.left - dx;
    }, {signal});
    const endDrag = event => {
      if (!drag || event.pointerId !== drag.id) return;
      const current = drag;
      drag = null;
      track.classList.remove('is-dragging');
      if (track.hasPointerCapture(current.id)) track.releasePointerCapture(current.id);
      if (!current.moving) return;
      const delta = track.scrollLeft - current.left;
      let target = Math.round(track.scrollLeft / track.clientWidth);
      if (target === current.index && Math.abs(delta) > Math.min(80, track.clientWidth * .18)) target += Math.sign(delta);
      goTo(target);
    };
    ['pointerup','pointercancel','lostpointercapture'].forEach(type => track.addEventListener(type, endDrag, {signal}));
    let width = track.clientWidth;
    const resize = new ResizeObserver(() => {
      if (width === track.clientWidth) return;
      width = track.clientWidth;
      goTo(index, false);
    });
    resize.observe(track);
    signal.addEventListener('abort', () => { resize.disconnect(); cancelAnimationFrame(frame); clearTimeout(scrollTimer); track.classList.remove('is-dragging'); }, {once:true});
    const controls = root.querySelector('[data-gallery-controls]');
    if (controls) controls.hidden = slides.length < 2;
    track.dataset.draggable = String(slides.length > 1);
    goTo(index, false);
    return (id, smooth = true) => {
      const target = slides.findIndex(slide => slide.dataset.media === String(id));
      if (target >= 0) goTo(target, smooth);
    };
  };

  // The floating control submits the original form, including its selected selling plan.
  const setupStickyPurchase = (root, signal) => {
    const template = root.querySelector('[data-sticky-purchase-template]');
    const form = root.querySelector('[data-nny-product-form]');
    const mainButton = root.querySelector('[data-add]');
    const mainQuantity = form?.querySelector('[name="quantity"]');
    const noop = {sync() {}, setStatus() {}};
    if (!template || !form || !mainButton || !mainQuantity) return noop;
    const bar = template.content.firstElementChild.cloneNode(true);
    const quantity = bar.querySelector('[data-sticky-quantity]');
    const minus = bar.querySelector('[data-sticky-minus]');
    const plus = bar.querySelector('[data-sticky-plus]');
    const add = bar.querySelector('[data-sticky-add]');
    const label = bar.querySelector('[data-sticky-label]');
    const status = bar.querySelector('[data-sticky-status]');
    const desktop = matchMedia('(min-width: 991px)');
    let observer, frame;
    const setStatus = text => {
      status.textContent = text;
      status.hidden = !text;
    };
    const sync = () => {
      for (const attr of ['min', 'max', 'step']) {
        if (mainQuantity.hasAttribute(attr)) quantity.setAttribute(attr, mainQuantity.getAttribute(attr));
        else quantity.removeAttribute(attr);
      }
      quantity.value = mainQuantity.value;
      const busy = mainButton.getAttribute('aria-busy') === 'true';
      add.disabled = mainButton.disabled;
      quantity.disabled = mainButton.disabled;
      bar.setAttribute('aria-busy', String(busy));
      label.textContent = busy ? 'ADDING…' : mainButton.disabled ? 'UNAVAILABLE' : root.querySelector('[data-plan]')?.value ? 'SUBSCRIBE' : 'ADD TO CART';
      const value = Number(quantity.value), min = Number(quantity.min || 1);
      minus.disabled = mainButton.disabled || !quantity.value || value <= min;
      plus.disabled = mainButton.disabled || (quantity.max !== '' && value >= Number(quantity.max));
    };
    const copyQuantity = () => {
      mainQuantity.value = quantity.value;
      mainQuantity.dispatchEvent(new Event('input', {bubbles:true}));
    };
    mainQuantity.addEventListener('input', () => { sync(); setStatus(''); }, {signal});
    mainQuantity.addEventListener('change', sync, {signal});
    quantity.addEventListener('input', copyQuantity, {signal});
    quantity.addEventListener('change', () => {
      copyQuantity();
      mainQuantity.dispatchEvent(new Event('change', {bubbles:true}));
    }, {signal});
    [[minus, -1], [plus, 1]].forEach(([button, direction]) => button.addEventListener('click', () => {
      if (!quantity.value || !Number.isFinite(quantity.valueAsNumber)) quantity.value = quantity.min || '1';
      else if (direction > 0) quantity.stepUp();
      else quantity.stepDown();
      copyQuantity();
      mainQuantity.dispatchEvent(new Event('change', {bubbles:true}));
    }, {signal}));
    const submit = () => {
      if (mainButton.disabled || !quantity.reportValidity()) return;
      copyQuantity();
      form.requestSubmit(mainButton);
    };
    add.addEventListener('click', submit, {signal});
    quantity.addEventListener('keydown', event => {
      if (event.key === 'Enter') { event.preventDefault(); submit(); }
    }, {signal});
    const headerBottom = () => Math.max(0, document.getElementById('header-group')?.getBoundingClientRect().bottom || 0);
    const updateVisibility = () => {
      if (signal.aborted) return;
      const rect = mainButton.getBoundingClientRect();
      // Do not show at the top of a long product hero, before reaching the main button.
      const visible = rect.height > 0 && rect.bottom <= headerBottom();
      if (!visible && !bar.hidden && bar.contains(document.activeElement)) {
        (document.activeElement === quantity ? mainQuantity : mainButton).focus({preventScroll:true});
      }
      bar.hidden = !visible;
    };
    const observeVisibility = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (signal.aborted) return;
        observer?.disconnect();
        observer = new IntersectionObserver(updateVisibility, {rootMargin:`-${Math.ceil(headerBottom())}px 0px 0px 0px`, threshold:[0, 1]});
        observer.observe(mainButton);
        updateVisibility();
      });
    };
    const mount = () => {
      if (signal.aborted) return;
      const dock = desktop.matches && document.querySelector('.nny-desktop-dock__container');
      const parent = dock || document.body;
      if (bar.parentElement !== parent) parent.append(bar);
      bar.classList.toggle('nny-sticky-purchase--docked', !!dock);
      document.body.classList.add('nny-has-sticky-purchase');
      observeVisibility();
    };
    desktop.addEventListener('change', mount, {signal});
    window.addEventListener('resize', observeVisibility, {passive:true, signal});
    window.addEventListener('pageshow', () => { sync(); mount(); }, {signal});
    document.addEventListener('shopify:section:load', mount, {signal});
    document.addEventListener('DOMContentLoaded', mount, {once:true, signal});
    const resize = new ResizeObserver(observeVisibility);
    resize.observe(root);
    const header = document.getElementById('header-group');
    if (header) resize.observe(header);
    signal.addEventListener('abort', () => {
      observer?.disconnect(); resize.disconnect(); cancelAnimationFrame(frame); bar.remove();
      if (!document.querySelector('[data-sticky-purchase]')) document.body.classList.remove('nny-has-sticky-purchase');
    }, {once:true});
    sync(); mount();
    return {sync, setStatus};
  };

  customElements.define('nny-product', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      const q = s => this.querySelector(s);
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const all = s => [...this.querySelectorAll(s)];
      const config = JSON.parse(q('[data-product-config]')?.textContent || '{"variants":[]}');
      const showMedia = setupGallery(this, signal);
      const stickyPurchase = setupStickyPurchase(this, signal);
      const variantSelect = q('[data-variant]');
      const planSelect = q('[data-plan]');
      const tabs = all('[role="tab"]');
      const selectTab = tab => tabs.forEach(t => {
        const selected = t === tab;
        t.setAttribute('aria-selected', String(selected)); t.tabIndex = selected ? 0 : -1;
        q('#' + CSS.escape(t.getAttribute('aria-controls'))).hidden = !selected;
      });
      tabs.forEach((t,i) => {
        t.addEventListener('click',()=>selectTab(t), {signal});
        t.addEventListener('keydown',e=>{
          let n=i;
          if(e.key==='ArrowRight') n=(i+1)%tabs.length;
          else if(e.key==='ArrowLeft') n=(i+tabs.length-1)%tabs.length;
          else if(e.key==='Home') n=0;
          else if(e.key==='End') n=tabs.length-1;
          else return;
          e.preventDefault(); selectTab(tabs[n]); tabs[n].focus();
        }, {signal});
      });
      q('[data-open-application]')?.addEventListener('click', e => {
        const tab = tabs.find(t => t.id.startsWith('NnyTab-application-'));
        if (!tab) return;
        e.preventDefault();
        selectTab(tab);
        tab.focus({preventScroll:true});
        tab.scrollIntoView({behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth',block:'center'});
      }, {signal});
      let preferredMode='once', preferredPlan='';
      const updatePrice = () => {
        const v = config.variants.find(v => v.id === variantSelect?.value);
        if (!v) return;
        const plan=v.plans.find(p=>p.id===planSelect.value);
        q('[data-price]').textContent=plan?.price || v.price;
        q('[data-once-price]').textContent=v.price;
        q('[data-sub-price]').textContent=(plan || v.plans[0])?.price || '';
        const saving=plan && v.priceCents>plan.priceCents;
        q('[data-compare-price]').hidden=!saving;
        q('[data-compare-price]').textContent=v.price;
        q('[data-save-badge]').hidden=!saving;
        q('[data-save-badge]').textContent=saving ? `SAVE ${Math.round((1-plan.priceCents/v.priceCents)*100)}% WITH REFILLS` : '';
        all('[data-purchase-mode]').forEach(r=>r.checked=r.value===(plan?'subscription':'once'));
        all('[data-plan-card]').forEach(c=>c.classList.toggle('selected',c.dataset.planCard===(plan?'subscription':'once')));
        q('[data-cadence-area]').hidden=!plan;
        q('[data-cadence-description]').textContent=plan?.name || '';
        all('[data-plan-id]').forEach(b=>b.setAttribute('aria-pressed',String(b.dataset.planId===plan?.id)));
        q('[data-purchase-summary]').textContent=plan ? `${plan.name} · ${plan.price} per unit. Recurring purchase; see plan terms at checkout.` : 'One-time purchase · no recurring delivery.';
        const canBuy=v.available && (!v.requiresPlan || !!plan);
        q('[data-add]').disabled=!canBuy || !!this.cartPending;
        q('[data-add-label]').textContent=canBuy ? (plan?'+ SUBSCRIBE':'+ ADD TO BAG') : 'UNAVAILABLE';
        q('[data-add-price]').textContent=plan?.price || v.price;
        q('[data-add-price]').hidden=!canBuy;
        q('[data-stock]').textContent=canBuy?'Available':'Unavailable';
        stickyPurchase.sync();
      };
      const updateVariant = (changeURL = true) => {
        const v=config.variants.find(v=>v.id===variantSelect?.value);
        if(!v) return;
        planSelect.replaceChildren();
        if(!v.requiresPlan) planSelect.add(new Option('One-time purchase',''));
        v.plans.forEach(p=>planSelect.add(new Option(`${p.name} — ${p.price}`,p.id)));
        const usePlan=v.requiresPlan || preferredMode==='subscription';
        planSelect.value=usePlan ? (v.plans.find(p=>p.id===preferredPlan) || v.plans[0])?.id || '' : '';
        planSelect.disabled=!v.plans.length;
        q('[data-plan-wrap]').hidden=true;
        q('[data-purchase-options]').hidden=!v.plans.length;
        q('[data-plan-card="once"]').hidden=v.requiresPlan;
        const cadences=q('[data-cadences]'); cadences.replaceChildren();
        v.plans.forEach(p=>{
          const button=document.createElement('button');button.type='button';button.dataset.planId=p.id;button.textContent=p.name;
          button.addEventListener('click',()=>{preferredPlan=p.id;preferredMode='subscription';planSelect.value=p.id;updatePrice();}, {signal});cadences.append(button);
        });
        const quantity=q('[name="quantity"]');quantity.min=v.min;quantity.step=v.step;quantity.value=v.min;
        if(v.max) quantity.max=v.max;else quantity.removeAttribute('max');
        showMedia(v.mediaId, changeURL); updatePrice();
        if(changeURL){const url=new URL(location.href);url.searchParams.set('variant',v.id);history.replaceState({},'',url);}
      };
      all('[data-purchase-mode]').forEach(r=>r.addEventListener('change',()=>{
        preferredMode=r.value;const v=config.variants.find(v=>v.id===variantSelect.value);
        planSelect.value=r.value==='subscription' ? (v.plans.find(p=>p.id===preferredPlan) || v.plans[0])?.id || '' : '';
        updatePrice();
      }, {signal}));
      variantSelect?.addEventListener('change',()=>updateVariant(), {signal});
      updateVariant(false);
      const productForm = q('[data-nny-product-form]');
      productForm?.addEventListener('submit', async event => {
        event.preventDefault();
        if (this.cartPending || !productForm.reportValidity()) return;
        const button = q('[data-add]');
        if (button.disabled) return;
        const purchaseTrigger = document.activeElement;
        const message = q('[data-cart-status]');
        this.cartPending = true;
        button.disabled = true;
        button.setAttribute('aria-busy','true');
        q('[data-add-label]').textContent = 'ADDING…';
        message.hidden = true;
        stickyPurchase.setStatus('');
        stickyPurchase.sync();
        let added = false;
        try {
          const fields = new FormData(productForm);
          fields.delete('return_to');
          const response = await fetch(this.dataset.cartAddUrl || `${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method:'POST', credentials:'same-origin', headers:{'Accept':'application/json'}, body:fields
          });
          const result = await response.json();
          if (!response.ok) throw new Error(result.description || result.message || 'Unable to add this item. Please try again.');
          added = true;
          if (typeof window.nnyOpenCartDrawer !== 'function') throw new Error('Your item was added. Open the bag from the header to continue.');
          const refreshed = await window.nnyOpenCartDrawer(purchaseTrigger);
          if (refreshed === false) throw new Error('Bag refresh failed.');
        } catch (error) {
          message.textContent = added ? 'Your item was added. Open the bag to review your order and check out.' : error.message;
          message.hidden = false;
          stickyPurchase.setStatus(message.textContent);
        } finally {
          this.cartPending = false;
          button.removeAttribute('aria-busy');
          updatePrice();
        }
      }, {signal});
      if(this.dataset.reviewsEnabled !== 'true') return;
      const proxy = this.dataset.proxy;
      const status = q('[data-review-status]');
      if(!/^\/[a-z0-9/_-]+$/i.test(proxy || '')) { status.textContent = 'Reviews are temporarily unavailable.'; return; }
      const base = proxy.replace(/\/$/,'');
      const pid = this.dataset.productId;
      let reviews = [], token = '';
      const request = async (path, options={}) => {
        const response = await fetch(`${base}${path}`,{credentials:'same-origin',...options});
        let data; try { data = await response.json(); } catch { throw new Error('Reviews are temporarily unavailable. Please try again later.'); }
        if(!response.ok) throw new Error(data.error || 'Unable to complete this request.');
        return data;
      };
      const renderReviews = () => {
        const list = q('[data-review-list]'); list.replaceChildren(); list.classList.remove('np-empty');
        const sorted = [...reviews];
        const sort = q('[data-review-sort]').value;
        if(sort !== 'newest') sorted.sort((a,b) => sort==='highest'?b.rating-a.rating:a.rating-b.rating);
        if(!sorted.length) { list.classList.add('np-empty'); list.textContent='Be the first to share your routine.'; }
        sorted.forEach(r => {
          const article=document.createElement('article'); article.className='np-review-card';
          const stars=document.createElement('div'); stars.className='np-stars'; stars.textContent='★'.repeat(r.rating)+'☆'.repeat(5-r.rating); stars.setAttribute('aria-label',`${r.rating} out of 5 stars`);
          const h=document.createElement('h3'); h.textContent=r.title;
          const body=document.createElement('p'); body.textContent=r.body;
          const meta=document.createElement('small'); meta.textContent=`${r.author} · Verified purchase${r.source==='merchant'?' · Added by N.N.Y with customer consent':''} · ${new Date(r.created_at*1000).toLocaleDateString()}`;
          article.append(stars,h,body,meta); list.append(article);
        });
      };
      q('[data-review-sort]').addEventListener('change',renderReviews, {signal});
      request(`/reviews?product_id=${encodeURIComponent(pid)}`).then(data=>{
        reviews=data.reviews; renderReviews();
        q('[data-review-summary]').textContent=data.count ? `${data.average.toFixed(1)} / 5 · ${data.count} verified reviews` : 'No published reviews yet.';
        status.textContent=data.count>reviews.length?'Showing the most recent 100 reviews.':'';
      }).catch(e=>{status.textContent=e.message;});
      q('[data-review-open]')?.addEventListener('click',async e=>{
        e.currentTarget.disabled=true;
        try { const data=await request(`/eligibility?product_id=${encodeURIComponent(pid)}`); token=data.token; q('[data-review-form]').hidden=false; status.textContent='Your purchase was verified. Your review will be moderated before publication.'; q('[name="author"]').focus(); }
        catch(err){status.textContent=err.message;}
        finally { q('[data-review-open]').disabled=false; }
      }, {signal});
      q('[data-review-form]').addEventListener('submit',async e=>{
        e.preventDefault(); const form=e.currentTarget; const submit=form.querySelector('[type="submit"]'); submit.disabled=true;
        const fields=Object.fromEntries(new FormData(form));
        try { await request('/reviews',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({...fields,product_id:pid,token})}); form.reset(); form.hidden=true; token=''; status.textContent='Thank you. Your review is awaiting moderation.'; }
        catch(err){status.textContent=err.message;}
        finally { submit.disabled=false; }
      }, {signal});
    }
    refresh() {
      this.controller?.abort();
      this.ready = false;
      this.connectedCallback();
    }
    disconnectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected) { this.controller?.abort(); this.ready = false; }
      });
    }
  });
})();

(() => {
  if (customElements.get('nny-comparison')) return;
  customElements.define('nny-comparison', class extends HTMLElement {
    connectedCallback() {
      if (this.compareReady) return;
      this.compareReady = true;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      const range = this.querySelector('[data-compare]');
      if (!range) return;
      const initial = Math.min(90, Math.max(10, Number(this.dataset.initial) || 50));
      const setPosition = (value, announce = false) => {
        range.value = String(value);
        this.style.setProperty('--split', `${value}%`);
        if (announce) range.setAttribute('aria-valuetext', `${Math.round(value)}% ${range.dataset.beforeLabel || 'before'}`);
      };
      const stop = () => {
        this.interacted = true;
        cancelAnimationFrame(this.frame);
        this.observer?.disconnect();
        setPosition(Number(range.value), true);
      };
      range.addEventListener('input', () => { stop(); setPosition(Number(range.value), true); }, {signal});
      ['pointerdown','keydown','focus'].forEach(type => range.addEventListener(type, stop, {signal}));
      setPosition(initial, true);
      const motion = matchMedia('(prefers-reduced-motion: reduce)');
      motion.addEventListener('change', () => { if (motion.matches) stop(); }, {signal});
      if (this.dataset.animate !== 'true' || motion.matches || !('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting) || this.interacted) return;
        this.observer.disconnect();
        let start;
        const animate = time => {
          if (this.interacted || !this.isConnected) return;
          start ??= time;
          const progress = Math.min(1, (time - start) / 2400);
          const position = Math.max(2, Math.min(98, initial + 32 * Math.sin(progress * Math.PI * 2) * Math.sin(progress * Math.PI)));
          setPosition(position);
          if (progress < 1) this.frame = requestAnimationFrame(animate);
          else setPosition(initial, true);
        };
        this.frame = requestAnimationFrame(animate);
      }, {threshold:0.35});
      this.observer.observe(this);
    }
    refresh() { this.disconnectedCallback(); this.connectedCallback(); }
    disconnectedCallback() {
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      this.abort?.abort();
      this.compareReady = false;
      this.interacted = false;
    }
  });
})();
